import { useMutation } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CircleAlert, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Spinner } from '@/Domain/components/Shared/Spinner';

type Props = {
  pendingEmbedCount: number;
  failedEmbedCount?: number;
};

// A small edit re-embeds well inside this, so only real waits get announced.
const PENDING_GRACE_MS = 2500;
const TRANSITION = { duration: 0.25, ease: [0.22, 1, 0.36, 1] } as const;

const useSustained = (active: boolean, delayMs: number): boolean => {
  const [sustained, setSustained] = useState(false);

  useEffect(() => {
    if (!active) {
      setSustained(false);
      return;
    }
    const timer = setTimeout(() => setSustained(true), delayMs);
    return () => clearTimeout(timer);
  }, [active, delayMs]);

  return sustained;
};

export const EmbedStatusIndicator = ({ pendingEmbedCount, failedEmbedCount = 0 }: Props) => {
  const { t } = useTranslation('topbar');
  const reduceMotion = useReducedMotion();
  const retry = useMutation<{ retried: number }, unknown, void>({
    mutationKey: [QueryKey.knowledgeBase.retryFailedEmbeds],
  });
  const pendingSustained = useSustained(pendingEmbedCount > 0, PENDING_GRACE_MS);
  // Gate on the live count too — the sustained flag clears a commit late, which would fade out a stale "0 articles" frame.
  const showPending = pendingEmbedCount > 0 && pendingSustained;
  // Failures are terminal, not transient, so they never wait out the grace period.
  const showFailed = failedEmbedCount > 0;
  const hidden = reduceMotion ? { opacity: 0 } : { opacity: 0, y: -3 };

  return (
    // Keep the live region mounted so screen readers announce later content changes.
    <div className="min-w-0" role="status" aria-live="polite">
      <AnimatePresence initial={false}>
        {(showPending || showFailed) && (
          <motion.div
            initial={hidden}
            animate={{ opacity: 1, y: 0 }}
            exit={hidden}
            transition={TRANSITION}
            className="flex items-center gap-3 min-w-0"
            data-testid="topbar-embed-status-indicator"
          >
            {showPending && (
              <div
                className="flex items-center gap-2 min-w-0"
                data-testid="topbar-embed-pending-indicator"
              >
                <Spinner className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span className="truncate text-sm text-muted-foreground">
                  {t('embed_processing', { count: pendingEmbedCount })}
                </span>
              </div>
            )}
            {showFailed && (
              <div
                className="flex items-center gap-1.5 min-w-0"
                data-testid="topbar-embed-failed-indicator"
              >
                <CircleAlert className="h-4 w-4 flex-shrink-0 text-destructive" />
                <span className="truncate text-sm text-destructive">
                  {t('embed_failed_count', { count: failedEmbedCount })}
                </span>
                <Button
                  variant="ghost"
                  size="xs"
                  className="h-6 gap-1 px-1.5 text-destructive hover:text-destructive"
                  onClick={() => retry.mutate()}
                  disabled={retry.isPending}
                  data-testid="topbar-embed-retry-button"
                >
                  {retry.isPending ? (
                    <Spinner className="h-3.5 w-3.5" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {t('embed_retry')}
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
